import {
  type FocusEvent,
  type FormEvent,
  type ReactNode,
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';
import clsx from 'clsx';
import CategorySelector from 'components/CategorySelector/CategorySelector';
import Checkbox from 'components/Checkbox/Checkbox';
import IconButton from 'components/IconButton/IconButton';
import TableCell from 'components/Table/TableCell';
import TableRow from 'components/Table/TableRow';
import TextField from 'components/TextField/TextField';
import {
  ALPHANUMERIC_AND_NUMBER_REGEX,
  BOM_INPUT_PRODUCT_REGEX,
  COMPANY_NAME_MIN_LENGTH,
  DATA_SETS_NAME_MAX_LENGTH,
} from 'constants/general';

import type IOption from 'components/MappingModal/interfaces/IOption';
import type IUpdateAgentSessionSupplierDiscoveryItemBody from 'store/slices/valueChainMap/interfaces/IUpdateAgentSessionSupplierDiscoveryItemBody';
import type {
  ISupplierRowDraft,
  TSupplierEditableCell,
  TSupplierFocusField,
} from './interfaces/IRowAICreatorSupplier';
import type IRowAICreatorSupplier from './interfaces/IRowAICreatorSupplier';

import styles from './sass/RowAICreatorSupplier.module.scss';

type TSupplierTextField = 'name' | 'code' | 'website';
type TSupplierValidatedField = 'name' | 'code' | 'website';
type TSupplierCountryField = 'location';

const WEBSITE_URL_REGEX = /^(https?:\/\/)?([\w-]+\.)+[\w]{2,}(\/[\w\-./?%&=]*)?$/;

const joinLocation = (city?: string | null, state?: string | null, country?: string | null) => (
  [city, state, country]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(', ')
);

const createDraftFromItem = (item: IRowAICreatorSupplier['item']): ISupplierRowDraft => ({
  ...item,
  isActive: item.isActive ?? true,
  name: item.name ?? '',
  code: item.code ?? '',
  website: item.website ?? '',
  industrySector: item.industrySector ?? '',
  goodsType: item.goodsType ?? '',
  addressCity: item.addressCity ?? '',
  addressState: item.addressState ?? '',
  addressCountry: item.addressCountry ?? '',
  hqAddressCity: item.hqAddressCity ?? '',
  hqAddressState: item.hqAddressState ?? '',
  hqAddressCountry: item.hqAddressCountry ?? '',
  source: item.source ?? '',
  location: joinLocation(item.addressCity, item.addressState, item.addressCountry),
  hq: joinLocation(item.hqAddressCity, item.hqAddressState, item.hqAddressCountry),
});

function RowAICreatorSupplier({
  item,
  naceOptions,
  countryOptions,
  onUpdate,
  onDelete,
  focusCell,
  onStartEdit,
}: IRowAICreatorSupplier) {
  const { t } = useTranslation();
  const rowRef = useRef<HTMLTableRowElement | null>(null);
  const nextEditingCellRef = useRef<TSupplierEditableCell | null>(null);
  const [editingCell, setEditingCell] = useState<TSupplierEditableCell | null>(null);
  const [draftItem, setDraftItem] = useState<ISupplierRowDraft>(() => createDraftFromItem(item));

  const normalizeText = useCallback((value?: string | null) => value?.trim() || '', []);

  const getFieldValidationError = useCallback((
    field: TSupplierValidatedField,
    value?: string | null,
  ) => {
    const normalizedValue = value?.trim() || '';

    if (!normalizedValue) {
      return '';
    }

    if (field === 'name' || field === 'code') {
      const fieldNameKey = field === 'name'
        ? 'suppliers.fields.supplierName'
        : 'suppliers.fields.supplierCode';

      if (normalizedValue.length < COMPANY_NAME_MIN_LENGTH) {
        return t('errors.minCharactersLength', {
          name: t(fieldNameKey),
          length: COMPANY_NAME_MIN_LENGTH,
        });
      }

      if (normalizedValue.length > DATA_SETS_NAME_MAX_LENGTH) {
        return t('errors.maxCharactersLength', {
          name: t(fieldNameKey),
          length: DATA_SETS_NAME_MAX_LENGTH,
        });
      }
    }

    if (field === 'name') {
      if (!BOM_INPUT_PRODUCT_REGEX.test(normalizedValue)) {
        return t('errors.nameContainLettersAndNumbers');
      }
    }

    if (
      field === 'code'
      && !ALPHANUMERIC_AND_NUMBER_REGEX.test(normalizedValue)
    ) {
      return t('errors.nameContainLettersAndNumbers');
    }

    if (field === 'website' && !WEBSITE_URL_REGEX.test(normalizedValue)) {
      return t('errors.invalidUrl');
    }

    return '';
  }, [t]);

  const getDraftValidationError = useCallback((
    draft: ISupplierRowDraft,
    fields: TSupplierValidatedField[],
  ) => {
    fields.forEach((field) => {
      const validationError = getFieldValidationError(field, draft[field] as string | null);

      if (validationError) {
        throw new Error(validationError);
      }
    });
  }, [getFieldValidationError]);

  const formatSourceLabel = useCallback((value?: string | null) => {
    const normalizedValue = value?.trim();

    if (!normalizedValue) {
      return '';
    }

    if (normalizedValue.toLowerCase() === 'ai') {
      return 'AI';
    }

    if (normalizedValue.toLowerCase() === 'import') {
      return 'Import';
    }

    if (normalizedValue.toLowerCase() === 'manual' || normalizedValue.toLowerCase() === 'manually') {
      return 'Manually';
    }

    return normalizedValue.charAt(0).toUpperCase() + normalizedValue.slice(1).toLowerCase();
  }, []);

  const buildRowBody = useCallback((draft: ISupplierRowDraft): IUpdateAgentSessionSupplierDiscoveryItemBody => ({
    isActive: draft.isActive,
    name: normalizeText(draft.name),
    code: normalizeText(draft.code),
    website: normalizeText(draft.website),
    addressCity: normalizeText(draft.addressCity),
    addressState: normalizeText(draft.addressState),
    addressCountry: normalizeText(draft.addressCountry),
    industrySector: normalizeText(draft.industrySector),
    hqAddressCity: normalizeText(draft.hqAddressCity),
    hqAddressState: normalizeText(draft.hqAddressState),
    hqAddressCountry: normalizeText(draft.hqAddressCountry),
    goodsType: normalizeText(draft.goodsType),
  }), [normalizeText]);

  const areBodiesEqual = (
    currentBody: IUpdateAgentSessionSupplierDiscoveryItemBody,
    nextBody: IUpdateAgentSessionSupplierDiscoveryItemBody,
  ) => JSON.stringify(currentBody) === JSON.stringify(nextBody);

  useEffect(() => {
    setDraftItem(createDraftFromItem(item));
  }, [item]);

  useEffect(() => {
    if (!editingCell) {
      return;
    }

    window.requestAnimationFrame(() => {
      const focusableElement = rowRef.current?.querySelector(
        [
          '[data-cell-editing="true"] input',
          '[data-cell-editing="true"] [class*="control"]',
        ].join(', '),
      ) as HTMLElement | null;

      focusableElement?.focus();
    });
  }, [editingCell]);

  useEffect(() => {
    if (!focusCell || focusCell.itemId !== item.id) {
      return;
    }

    window.requestAnimationFrame(() => {
      rowRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
  }, [focusCell, item.id]);

  const itemBody = buildRowBody(createDraftFromItem(item));
  const focusedField = focusCell != null && focusCell.itemId === item.id ? focusCell.field : null;
  const naceValue = useMemo(
    () => naceOptions.find((option) => option.value === draftItem.industrySector) || null,
    [draftItem.industrySector, naceOptions],
  );
  const getCountryOption = useCallback((value?: string | null) => {
    const normalizedCountry = value?.trim();

    if (!normalizedCountry) {
      return null;
    }

    const normalizedValue = normalizedCountry.toLowerCase();

    return countryOptions.find((option) => (
      option.value.toLowerCase() === normalizedValue
      || option.label?.toLowerCase() === normalizedValue
      || option.name?.toLowerCase() === normalizedValue
    )) || {
      value: normalizedCountry,
      label: normalizedCountry,
    };
  }, [countryOptions]);
  const locationCountryValue = useMemo(
    () => getCountryOption(draftItem.addressCountry),
    [draftItem.addressCountry, getCountryOption],
  );
  const getOptionLabel = useCallback((options: IOption[], value?: string) => (
    options.find((option) => option.value === value)?.label || value || ''
  ), []);

  const getCountryDisplayLabel = useCallback((value?: string | null) => (
    getCountryOption(value)?.label || value || ''
  ), [getCountryOption]);

  const getLocationDisplayLabel = useCallback((field: 'location' | 'hq') => {
    if (field === 'location') {
      return joinLocation(
        draftItem.addressCity,
        draftItem.addressState,
        getCountryDisplayLabel(draftItem.addressCountry),
      );
    }

    return joinLocation(
      draftItem.hqAddressCity,
      draftItem.hqAddressState,
      getCountryDisplayLabel(draftItem.hqAddressCountry),
    );
  }, [
    draftItem.addressCity,
    draftItem.addressCountry,
    draftItem.addressState,
    draftItem.hqAddressCity,
    draftItem.hqAddressCountry,
    draftItem.hqAddressState,
    getCountryDisplayLabel,
  ]);

  const applyEditingState = useCallback((nextEditingCell: TSupplierEditableCell | null = null) => {
    setEditingCell(nextEditingCell);
  }, []);

  const isCellFocused = useCallback((cell: TSupplierFocusField) => (
    focusedField === cell
  ), [focusedField]);

  const commitRow = useCallback(async (
    nextDraft: ISupplierRowDraft,
    nextEditingCell: TSupplierEditableCell | null = null,
  ) => {
    const currentEditingCell = editingCell;
    const fieldsToValidate = (editingCell === 'name' || editingCell === 'code' || editingCell === 'website')
      ? [editingCell] as TSupplierValidatedField[]
      : [];

    if (nextDraft.isActive && fieldsToValidate.length) {
      let validationError = '';

      try {
        getDraftValidationError(nextDraft, fieldsToValidate);
      } catch (error) {
        validationError = error instanceof Error ? error.message : '';
      }

      if (validationError) {
        toast.error(validationError);
        window.requestAnimationFrame(() => {
          const focusableElement = rowRef.current?.querySelector(
            '[data-cell-editing="true"] input',
          ) as HTMLElement | null;

          focusableElement?.focus();
        });

        return false;
      }
    }

    const nextBody = buildRowBody(nextDraft);

    if (areBodiesEqual(itemBody, nextBody)) {
      applyEditingState(nextEditingCell);
      return true;
    }

    try {
      applyEditingState(nextEditingCell);
      await onUpdate(item.id, nextBody);
      return true;
    } catch {
      applyEditingState(currentEditingCell);
      return false;
    }
  }, [applyEditingState, buildRowBody, editingCell, getDraftValidationError, item.id, itemBody, onUpdate]);

  const handleStartEdit = useCallback((cell: TSupplierEditableCell) => {
    onStartEdit?.();
    setEditingCell(cell);
  }, [onStartEdit]);

  const handleDraftTextChange = useCallback((field: TSupplierTextField, value: string) => {
    setDraftItem((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleSelectChange = useCallback((value: string) => {
    const nextDraft = {
      ...draftItem,
      industrySector: value,
    };

    setDraftItem(nextDraft);
    commitRow(nextDraft);
  }, [commitRow, draftItem]);

  const handleCountrySelectChange = useCallback((_field: TSupplierCountryField, option: IOption | null) => {
    const nextCountry = option?.value || '';
    const nextCountryLabel = option?.label || option?.name || nextCountry;
    const nextDraft = {
      ...draftItem,
      addressCountry: nextCountry,
      location: joinLocation(draftItem.addressCity, draftItem.addressState, nextCountryLabel),
    };

    setDraftItem(nextDraft);
    commitRow(nextDraft);
  }, [commitRow, draftItem]);

  const handleToggleActive = async () => {
    const previousDraft = draftItem;
    const nextIsActive = !draftItem.isActive;
    const nextDraft = {
      ...draftItem,
      isActive: nextIsActive,
    };

    setDraftItem(nextDraft);
    try {
      await onUpdate(item.id, { isActive: nextIsActive });
    } catch {
      setDraftItem(previousDraft);
    }
  };

  const handleDelete = () => {
    onDelete(item.id);
  };

  const handleCellBlur = (event: FocusEvent<HTMLDivElement>) => {
    const currentCell = event.currentTarget;

    if (!editingCell) {
      return;
    }

    const nextEditingCell = nextEditingCellRef.current;
    nextEditingCellRef.current = null;

    const relatedTarget = event.relatedTarget as Node | null;
    const focusMovedInside = relatedTarget ? currentCell.contains(relatedTarget) : false;

    if (!nextEditingCell && focusMovedInside) {
      return;
    }

    commitRow(draftItem, nextEditingCell);
  };

  const handleTextSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    commitRow(draftItem);
  };

  const renderViewCell = (
    cell: TSupplierEditableCell,
    value: ReactNode,
    className?: string,
  ) => (
    <button
      type="button"
      className={styles.cellButton}
      aria-label={t('valueChainMap.supplierUnitsEditCell')}
      onMouseDown={() => {
        nextEditingCellRef.current = editingCell && editingCell !== cell ? cell : null;
      }}
      onClick={(event) => {
        event.stopPropagation();

        if (editingCell && editingCell !== cell) {
          return;
        }

        handleStartEdit(cell);
      }}
    >
      <span className={className}>{value || <span className={styles.placeholder}>-</span>}</span>
    </button>
  );

  const renderReadOnlyCell = (value: ReactNode, className?: string) => (
    <div className={styles.cellInner} data-cell-editing={false}>
      <span className={className}>{value || <span className={styles.placeholder}>-</span>}</span>
    </div>
  );

  const renderTextCell = (
    cell: TSupplierEditableCell,
    field: TSupplierTextField,
    maxLength = DATA_SETS_NAME_MAX_LENGTH,
    className = styles.cellText,
  ) => (
    <div
      className={clsx(
        styles.cellInner,
        {
          [styles.errorCell]:
            isCellFocused(cell) && editingCell !== cell,
        },
      )}
      data-cell-editing={editingCell === cell}
      onBlurCapture={handleCellBlur}
    >
      {editingCell === cell ? (
        <form className={styles.formField} onSubmit={handleTextSubmit}>
          <TextField
            name={`supplier-item-${cell}-${item.id}`}
            value={String(draftItem[field] ?? '')}
            onChange={(event) => handleDraftTextChange(field, event.target.value)}
            className={styles.textField}
            maxLength={maxLength}
          />
        </form>
      ) : renderViewCell(cell, draftItem[field] as ReactNode, className)}
    </div>
  );

  const renderCountryCell = (
    cell: TSupplierCountryField,
    value: IOption | null,
  ) => (
    <div
      className={clsx(
        styles.cellInner,
        {
          [styles.errorCell]:
            isCellFocused(cell) && editingCell !== cell,
        },
      )}
      data-cell-editing={editingCell === cell}
      onBlurCapture={handleCellBlur}
    >
      {editingCell === cell ? (
        <CategorySelector
          value={value}
          options={countryOptions}
          onChange={(nextValue) => {
            handleCountrySelectChange(cell, nextValue);
          }}
          className={styles.selectField}
          placeholder={t('common.select')}
          searchPlaceholder={t('common.searchByName')}
          openByDefault
          formatLabel={(label) => label}
        />
      ) : renderViewCell(
        cell,
        getLocationDisplayLabel(cell),
        styles.addressValue,
      )}
    </div>
  );

  return (
    <TableRow ref={rowRef} className={styles.row}>
      <TableCell className={styles.checkboxCell}>
        <Checkbox
          name={`supplier-item-active-${item.id}`}
          value={item.id}
          checked={draftItem.isActive}
          onChange={handleToggleActive}
          className={styles.checkbox}
        />
      </TableCell>
      <TableCell className={styles.nameCell}>
        {renderTextCell('name', 'name', DATA_SETS_NAME_MAX_LENGTH, styles.primaryValue)}
      </TableCell>
      <TableCell className={styles.codeCell}>
        {renderTextCell('code', 'code')}
      </TableCell>
      <TableCell className={styles.locationCell}>
        {renderCountryCell('location', locationCountryValue)}
      </TableCell>
      <TableCell className={styles.hqCell}>
        {renderReadOnlyCell(getLocationDisplayLabel('hq'), styles.addressValue)}
      </TableCell>
      <TableCell className={styles.goodsTypeCell}>
        {renderReadOnlyCell(draftItem.goodsType as ReactNode, styles.cellText)}
      </TableCell>
      <TableCell className={styles.websiteCell}>
        {renderTextCell('website', 'website')}
      </TableCell>
      <TableCell className={styles.naceCell}>
        <div
          className={clsx(
            styles.cellInner,
            {
              [styles.errorCell]:
                isCellFocused('industrySector') && editingCell !== 'industrySector',
            },
          )}
          data-cell-editing={editingCell === 'industrySector'}
          onBlurCapture={handleCellBlur}
        >
          {editingCell === 'industrySector' ? (
            <CategorySelector
              value={naceValue}
              options={naceOptions}
              onChange={(nextValue) => {
                handleSelectChange(nextValue?.value || '');
              }}
              className={styles.selectField}
              placeholder={t('common.select')}
              searchPlaceholder={t('common.searchByName')}
              openByDefault
            />
          ) : renderViewCell(
            'industrySector',
            getOptionLabel(naceOptions, draftItem.industrySector),
            styles.cellText,
          )}
        </div>
      </TableCell>
      <TableCell className={styles.sourceCell}>
        <div className={styles.cellInner} data-cell-editing={false}>
          <div className={styles.sourceValueWrap}>
            {draftItem.source ? (
              <span className={styles.sourceBadge}>{formatSourceLabel(draftItem.source)}</span>
            ) : <span className={styles.placeholder}>-</span>}
          </div>
        </div>
      </TableCell>
      <TableCell className={styles.actionsCell}>
        <IconButton
          icon={<DeleteIcon />}
          onClick={handleDelete}
          className={styles.deleteButton}
        />
      </TableCell>
    </TableRow>
  );
}

export default RowAICreatorSupplier;
